import { TimeEntry, Activity, Customer, Project } from '../types/TimeEntry';
import { SharePointItem, sharepointService } from './SharePointService';

interface CacheEntry<T> {
  data: T;
  expiry: number;
}

class TimeRegistrationService {
  private timeListName = '/Lists/Time';
  private activityListName = '/Lists/Activity';
  private customerListName = '/Lists/Customer';
  private projectListName = '/Lists/Projects';
  private activityCustomerListName = '/Lists/Activity_Customer';
  private externalUsersProjectsListName = '/Lists/ExternalUsersProjects';
  private usersListName = '/Lists/Users';

  // Activities/Customers/Projects change rarely - caching them in memory for a short window avoids
  // re-fetching from SharePoint every time a modal opens within the same session.
  private readonly LOOKUP_CACHE_TTL_MS = 5 * 60 * 1000;
  private lookupCache = new Map<string, CacheEntry<any>>();
  private pendingLookupRequests = new Map<string, Promise<any>>();

  private async getCachedOrFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const cached = this.lookupCache.get(key);
    if (cached && cached.expiry > Date.now()) {
      return cached.data as T;
    }

    const pending = this.pendingLookupRequests.get(key);
    if (pending) {
      return pending as Promise<T>;
    }

    const request = fetcher()
      .then((data) => {
        this.lookupCache.set(key, { data, expiry: Date.now() + this.LOOKUP_CACHE_TTL_MS });
        return data;
      })
      .finally(() => {
        this.pendingLookupRequests.delete(key);
      });

    this.pendingLookupRequests.set(key, request);
    return request;
  }

  // Call after activities/customers/projects/allowed-projects are edited elsewhere so the next
  // lookup reflects the change instead of serving stale cached data for up to LOOKUP_CACHE_TTL_MS.
  clearLookupCache(): void {
    this.lookupCache.clear();
  }

  private transformTimeEntry(item: any): TimeEntry {
    return {
      Id: item.Id,
      Title: item.Title || '',
      Date: item.Date ? item.Date.split('T')[0] : '',
      Hours: parseFloat(item.Hours) || 0,
      Activity: {
        Id: item.Activity?.Id || 0,
        Title: item.Activity?.Title || 'Unknown Activity'
      },
      Customer: {
        Id: item.Customer?.Id || 0,
        Title: item.Customer?.Title || 'Unknown Customer'
      },
      Person: {
        Id: item.Person?.Id || 0,
        Title: item.Person?.Title || 'Unknown Person',
        EMail: item.Person?.EMail || '',
        LoginName: item.Person?.LoginName || ''
      },
      ...(item.Project && {
        Project: {
          Id: item.Project.Id,
          Title: item.Project.Title
        }
      }),
      IsInvoiced: item.IsInvoiced || false,
      // Nya poster utan värde ska räknas som fakturerbara (kolumnens SharePoint-default är Ja)
      Billable: item.Fakturerbart !== false,
      Details: item.Details,
      CreatedDate: item.Created,
      ModifiedDate: item.Modified
    };
  }

  private async getAllTimeEntriesMatchingFilter(filter: string | undefined, accessToken: string): Promise<TimeEntry[]> {
    const pageSize = 5000;
    const items: SharePointItem[] = [];
    let lastId = 0;

    while (true) {
      const idFilter = `Id gt ${lastId}`;
      const pageFilter = filter ? `(${filter}) and ${idFilter}` : idFilter;
      const response = await sharepointService.getListItems(this.timeListName, accessToken, {
        $filter: pageFilter,
        $select: 'Id,Title,Details,Date,Hours,IsInvoiced,Fakturerbart,Activity/Id,Activity/Title,Customer/Id,Customer/Title,Project/Id,Project/Title,Person/Id,Person/Title,Person/EMail,Created,Modified',
        $expand: 'Activity,Customer,Person,Project',
        $orderby: 'Id asc',
        $top: pageSize
      });

      if (!response.success) {
        throw new Error(response.error || 'Kunde inte läsa tidsregistreringar från SharePoint');
      }

      const page = response.data;
      items.push(...page);
      if (page.length < pageSize) {
        break;
      }

      const nextId = Number(page[page.length - 1]?.Id);
      if (!Number.isInteger(nextId) || nextId <= lastId) {
        throw new Error('Kunde inte fortsätta läsa tidsregistreringar från SharePoint');
      }
      lastId = nextId;
    }

    return items
      .map((item) => this.transformTimeEntry(item))
      .sort((first, second) => second.Date.localeCompare(first.Date) || second.Id - first.Id);
  }

  async getTimeEntries(year: number, month: number, accessToken: string): Promise<TimeEntry[]> {
    try {
      const currentUser = await this.getCurrentUserInfo(accessToken);
      
      // Calculate start date: first day of the month (local time, then convert to UTC string)
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      
      // Calculate end date: last day of the month (local time, then convert to UTC string)
      const nextMonthDate = new Date(year, month + 1, 1);
      const lastDayOfMonth = new Date(nextMonthDate.getTime() - 1).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`;

      const queryParams = {
        $filter: `Date ge datetime'${startDate}T00:00:00Z' and Date le datetime'${endDate}T23:59:59Z' and Person/Id eq ${currentUser.Id}`,
        $select: 'Id,Title,Details,Date,Hours,IsInvoiced,Fakturerbart,Activity/Id,Activity/Title,Customer/Id,Customer/Title,Project/Id,Project/Title,Person/Id,Person/Title,Person/EMail,Created,Modified',
        $expand: 'Activity,Customer,Person,Project',
        $orderby: 'Date desc'
      };

      const response = await sharepointService.getListItems(this.timeListName, accessToken, queryParams);

      if (response.success) {
        return response.data.map((item: any) => this.transformTimeEntry(item));
      }
      throw new Error(response.error || 'Failed to fetch time entries');
    } catch (error) {
      console.error('[TimeEntries]', error);
      throw error;
    }
  }

  async getAllTimeEntries(year: number, month: number, accessToken: string): Promise<TimeEntry[]> {
    try {
      // Calculate start date: first day of the month (local time, then convert to UTC string)
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      
      // Calculate end date: last day of the month (local time, then convert to UTC string)
      const nextMonthDate = new Date(year, month + 1, 1);
      const lastDayOfMonth = new Date(nextMonthDate.getTime() - 1).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`;

      const filter = `Date ge datetime'${startDate}T00:00:00Z' and Date le datetime'${endDate}T23:59:59Z'`;
      return await this.getAllTimeEntriesMatchingFilter(filter, accessToken);
    } catch (error) {
      console.error('[AllTimeEntries]', error);
      throw error;
    }
  }

  async getAllTimeEntriesForYear(year: number, accessToken: string): Promise<TimeEntry[]> {
    try {
      const filter = `Date ge datetime'${year}-01-01T00:00:00Z' and Date le datetime'${year}-12-31T23:59:59Z'`;
      return await this.getAllTimeEntriesMatchingFilter(filter, accessToken);
    } catch (error) {
      console.error('[AllTimeEntriesForYear]', error);
      throw error;
    }
  }

  async getAllTimeEntriesAllMonths(accessToken: string): Promise<TimeEntry[]> {
    try {
      const queryParams = {
        $select: 'Id,Title,Details,Date,Hours,IsInvoiced,Fakturerbart,Activity/Id,Activity/Title,Customer/Id,Customer/Title,Project/Id,Project/Title,Person/Id,Person/Title,Person/EMail,Created,Modified',
        $expand: 'Activity,Customer,Person,Project',
        $orderby: 'Date desc',
        $top: 5000
      };

      const response = await sharepointService.getListItems(this.timeListName, accessToken, queryParams);

      if (response.success) {
        return response.data.map((item: any) => this.transformTimeEntry(item));
      }
      throw new Error(response.error || 'Failed to fetch all time entries');
    } catch (error) {
      console.error('[AllTimeEntriesAllMonths]', error);
      throw error;
    }
  }

  async getActivities(accessToken: string): Promise<Activity[]> {
    return this.getCachedOrFetch('activities', async () => {
      try {
        const queryParams = {
          $select: 'Id,Title',
          $orderby: 'Title',
          $top: 1000
        };

        const response = await sharepointService.getListItems(this.activityListName, accessToken, queryParams);

        if (response.success) {
          return response.data.map((item: any) => ({
            Id: item.Id,
            Title: item.Title,
            IsActive: true
          }));
        }
        throw new Error(response.error || 'Failed to fetch activities');
      } catch (error) {
        console.error('[Activities]', error);
        throw error;
      }
    });
  }

  async getCustomers(accessToken: string): Promise<Customer[]> {
    return this.getCachedOrFetch('customers', async () => {
      try {
        const queryParams = {
          $select: 'Id,Title',
          $orderby: 'Title',
          $top: 1000
        };

        const response = await sharepointService.getListItems(this.customerListName, accessToken, queryParams);

        if (response.success) {
          return response.data.map((item: any) => ({
            Id: item.Id,
            Title: item.Title,
            IsActive: true
          }));
        }
        throw new Error(response.error || 'Failed to fetch customers');
      } catch (error) {
        console.error('[Customers]', error);
        throw error;
      }
    });
  }

  async getProjects(accessToken: string): Promise<Project[]> {
    return this.getCachedOrFetch('projects', async () => {
      try {
        const queryParams = {
          $select: 'Id,Title,Status,Customer/Id,Customer/Title',
          $expand: 'Customer',
          $filter: "Status eq 'Pågående'",
          $orderby: 'Title',
          $top: 1000
        };

        const response = await sharepointService.getListItems(this.projectListName, accessToken, queryParams);

        if (response.success) {
          return response.data.map((item: any) => ({
            Id: item.Id,
            Title: item.Title,
            Status: item.Status,
            IsActive: true,
            Customer: item.Customer ? {
              Id: item.Customer.Id,
              Title: item.Customer.Title
            } : undefined
          }));
        }
        throw new Error(response.error || 'Failed to fetch projects');
      } catch (error) {
        console.error('[Projects]', error);
        throw error;
      }
    });
  }

  // Returns the project ids an external user is restricted to (empty array = unrestricted/internal user).
  async getAllowedProjectIdsForUser(userId: number, accessToken: string): Promise<number[]> {
    return this.getCachedOrFetch(`allowedProjects:${userId}`, async () => {
      try {
        const queryParams = {
          $filter: `User/Id eq ${userId}`,
          $select: 'Project/Id',
          $expand: 'Project',
          $top: 1000
        };
        const response = await sharepointService.getListItems(this.externalUsersProjectsListName, accessToken, queryParams);
        if (!response.success) return [];

        const ids = response.data
          .map((item: any) => item.Project?.Id)
          .filter((id: any): id is number => typeof id === 'number');
        return Array.from(new Set(ids));
      } catch (error) {
        console.error('[AllowedProjectIdsForUser]', error);
        return [];
      }
    });
  }

  // Looks up the signed-in user's role in /Lists/Users. Returns null if there's no matching row
  // (caller must treat that as "no access" - this list is the source of truth for what a user can see).
  async getUserType(userId: number, accessToken: string): Promise<'Ägare' | 'Medarbetare' | 'Extern' | null> {
    try {
      const queryParams = {
        $filter: `User/Id eq ${userId}`,
        $select: 'Usertype',
        $top: 1
      };
      const response = await sharepointService.getListItems(this.usersListName, accessToken, queryParams);
      if (!response.success || !response.data || response.data.length === 0) {
        return null;
      }

      const usertype = response.data[0].Usertype;
      return usertype === 'Ägare' || usertype === 'Medarbetare' || usertype === 'Extern' ? usertype : null;
    } catch (error) {
      console.error('[UserType]', error);
      return null;
    }
  }

  async getCustomersForProject(projectId: number, accessToken: string): Promise<Customer[]> {
    try {
      // Fetch the project to get its associated customer
      const projects = await this.getProjects(accessToken);
      const project = projects.find(p => p.Id === projectId);
      
      if (!project || !project.Customer) {
        return [];
      }

      // Return the customer associated with this project
      return [{
        Id: project.Customer.Id,
        Title: project.Customer.Title,
        IsActive: true
      }];
    } catch (error) {
      console.error('[CustomersForProject]', error);
      throw error;
    }
  }

  async getActivitiesForCustomer(customerId: number, accessToken: string, projectId?: number): Promise<Activity[]> {
    return this.getCachedOrFetch(`activitiesForCustomer:${customerId}:${projectId ?? 'none'}`, async () => {
      try {
        const queryParams = {
          $filter: `Customer/Id eq ${customerId}`,
          $select: 'Activity/Id,Activity/Title,Project/Id',
          $expand: 'Activity,Project',
          $orderby: 'Activity/Title',
          $top: 1000
        };

        const response = await sharepointService.getListItems(this.activityCustomerListName, accessToken, queryParams);

        if (response.success) {
          const relevantItems = projectId
            ? (() => {
                const itemsForSelectedProject = response.data.filter((item: any) => item?.Project?.Id === projectId);

                // If the selected project has explicit mappings, only show those activities.
                if (itemsForSelectedProject.length > 0) {
                  return itemsForSelectedProject;
                }

                // If no mapping exists for the selected project, show only activities without project mapping.
                return response.data.filter((item: any) => !item?.Project?.Id);
              })()
            : response.data;

          // Remove duplicates and filter out null activities
          const uniqueActivities = new Map<number, Activity>();
          relevantItems.forEach((item: any) => {
            if (item.Activity && item.Activity.Id) {
              uniqueActivities.set(item.Activity.Id, {
                Id: item.Activity.Id,
                Title: item.Activity.Title,
                IsActive: true
              });
            }
          });
          return Array.from(uniqueActivities.values());
        }
        throw new Error(response.error || 'Failed to fetch activities for customer');
      } catch (error) {
        console.error('[ActivitiesForCustomer]', error);
        throw error;
      }
    });
  }

  async addTimeEntry(entry: Omit<TimeEntry, 'Id'>, accessToken: string): Promise<TimeEntry> {
    try {
      const spItemData: any = {
        Date: entry.Date + 'T00:00:00Z',
        Hours: entry.Hours,
        ActivityId: entry.Activity?.Id,
        CustomerId: entry.Customer?.Id,
        Title: entry.Title || '',
      };

      if (entry.Details) spItemData.Details = entry.Details;
      if (entry.Person?.Id > 0) spItemData.PersonId = entry.Person.Id;
      if (entry.Project?.Id) spItemData.ProjectId = entry.Project.Id;

      const response = await sharepointService.createListItem(this.timeListName, spItemData, accessToken);

      if (response.success) {
        return {
          ...entry,
          Id: response.data.Id,
          CreatedDate: response.data.Created,
          ModifiedDate: response.data.Modified
        };
      }
      throw new Error(response.error || 'Failed to create time entry');
    } catch (error) {
      console.error('[AddTimeEntry]', error);
      throw error;
    }
  }

  async updateTimeEntry(entryId: number, updates: Partial<TimeEntry>, accessToken: string): Promise<TimeEntry> {
    try {
      const spUpdateData: any = {};

      if (updates.Date) spUpdateData.Date = updates.Date + 'T00:00:00Z';
      if (updates.Hours !== undefined) spUpdateData.Hours = updates.Hours;
      if (updates.Activity) spUpdateData.ActivityId = updates.Activity.Id;
      if (updates.Customer) spUpdateData.CustomerId = updates.Customer.Id;
      if (updates.Person?.Id) spUpdateData.PersonId = updates.Person.Id;
      if (updates.Project?.Id) spUpdateData.ProjectId = updates.Project.Id;
      if (updates.Title !== undefined) spUpdateData.Title = updates.Title;
      if (updates.Details !== undefined) spUpdateData.Details = updates.Details;

      const response = await sharepointService.updateListItem(this.timeListName, entryId, spUpdateData, accessToken);

      if (!response.success) {
        throw new Error(response.error || 'Failed to update time entry');
      }

      return { ...updates, Id: entryId } as TimeEntry;
    } catch (error) {
      console.error('[UpdateTimeEntry]', error);
      throw error;
    }
  }

  async deleteTimeEntry(entryId: number, accessToken: string): Promise<void> {
    try {
      const response = await sharepointService.deleteListItem(this.timeListName, entryId, accessToken);

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete time entry');
      }
    } catch (error) {
      console.error('[DeleteTimeEntry]', error);
      throw error;
    }
  }

  async updateTimeEntriesInvoiceStatus(
    entries: TimeEntry[],
    isInvoiced: boolean,
    accessToken: string
  ): Promise<void> {
    try {
      const updates = entries.map(entry =>
        sharepointService.updateListItem(
          this.timeListName,
          entry.Id,
          { IsInvoiced: isInvoiced },
          accessToken
        )
      );

      const results = await Promise.all(updates);
      const hasErrors = results.some(result => !result.success);

      if (hasErrors) {
        throw new Error('Failed to update some time entries');
      }
    } catch (error) {
      console.error('[UpdateTimeEntriesInvoiceStatus]', error);
      throw error;
    }
  }

  async updateTimeEntriesBillableStatus(
    entries: TimeEntry[],
    billable: boolean,
    accessToken: string
  ): Promise<void> {
    try {
      const updates = entries.map(entry =>
        sharepointService.updateListItem(
          this.timeListName,
          entry.Id,
          { Fakturerbart: billable },
          accessToken
        )
      );

      const results = await Promise.all(updates);
      const hasErrors = results.some(result => !result.success);

      if (hasErrors) {
        throw new Error('Failed to update some time entries');
      }
    } catch (error) {
      console.error('[UpdateTimeEntriesBillableStatus]', error);
      throw error;
    }
  }

  async getCurrentUserInfo(accessToken: string): Promise<{ Id: number; Title: string; EMail: string; LoginName: string }> {
    try {
      const response = await sharepointService.getCurrentUser(accessToken);
      if (response.success && response.data) {
        return {
          Id: response.data.id || response.data.Id || 0,
          Title: response.data.displayName || response.data.Title || '',
          EMail: response.data.email || response.data.Email || '',
          LoginName: response.data.loginName || response.data.LoginName || ''
        };
      }
      throw new Error(response.error || 'Failed to get current user');
    } catch (error) {
      console.error('[CurrentUser]', error);
      throw error;
    }
  }

  groupEntriesByDate(entries: TimeEntry[]): { [date: string]: TimeEntry[] } {
    return entries.reduce((acc, entry) => {
      const date = entry.Date;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(entry);
      return acc;
    }, {} as { [date: string]: TimeEntry[] });
  }

  calculateDayTotal(entries: TimeEntry[]): number {
    return entries.reduce((total, entry) => total + entry.Hours, 0);
  }

  calculateMonthTotal(entries: TimeEntry[]): number {
    return entries.reduce((total, entry) => total + entry.Hours, 0);
  }
}

export const timeRegistrationService = new TimeRegistrationService();